/**
 *
 * CheckIns
 *
 * Admin tab for the six-monthly check-in mailer. Shows the history of
 * previous sends and a button to dispatch a new batch. See API
 * ADR-0002.
 *
 */
import * as React from 'react';
import { useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useCheckInsSlice } from './slice';
import {
  selectBatches,
  selectEligibleCount,
  selectSendBatch,
} from './slice/selectors';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { useConfirm } from '../../components/ConfirmDialog';
import { useSnackbar } from 'notistack';
import { useHasChanged } from '../../../utils/usePrevious';

export function CheckIns() {
  const dispatch = useDispatch();
  const { actions } = useCheckInsSlice();
  const batches = useSelector(selectBatches);
  const eligibleCount = useSelector(selectEligibleCount);
  const sendBatch = useSelector(selectSendBatch);
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    dispatch(actions.loadBatchesRequest());
    dispatch(actions.loadEligibleCountRequest());
  }, [dispatch, actions]);

  const sendBatchLoadingChanged = useHasChanged(sendBatch.loading);
  useEffect(() => {
    if (sendBatchLoadingChanged && !sendBatch.loading) {
      if (sendBatch.error) {
        enqueueSnackbar('Failed to queue the check-in batch.', {
          variant: 'error',
        });
      } else if (sendBatch.data) {
        enqueueSnackbar(
          'Check-in batch queued — sends will be processed in the background.',
          { variant: 'success' },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendBatchLoadingChanged, sendBatch.loading]);

  const onSend = () => {
    const count = eligibleCount.data ?? 0;
    confirm({
      title: `Send check-in email to ${count} ${
        count === 1 ? 'person' : 'people'
      }?`,
      description:
        'Every active person with an email address will receive the six-monthly check-in. They confirm they are still on the database with a single click. This cannot be undone once queued.',
      confirmationText: 'Send now',
    })
      .then(() => dispatch(actions.sendBatchRequest()))
      .catch(() => {});
  };

  return (
    <Box sx={{ my: 4 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h3" color="primary" gutterBottom>
            Six-monthly check-ins
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Sends the check-in email to every active person with an email
            address. Responses feed into the activity (traffic-light) score
            shown on the people list.
          </Typography>
        </Box>

        <Box>
          <Button
            variant="contained"
            color="primary"
            size="large"
            disabled={
              eligibleCount.loading || sendBatch.loading || !eligibleCount.data
            }
            onClick={onSend}
          >
            {eligibleCount.loading
              ? 'Loading…'
              : `Send check-in to ${eligibleCount.data ?? 0} people`}
          </Button>
        </Box>

        <Paper>
          <Box sx={{ p: 2 }}>
            <Typography variant="h5">Previous sends</Typography>
          </Box>
          {batches.loading && <LoadingIndicator />}
          {!batches.loading && batches.data.length === 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No check-in batches have been sent yet.
              </Typography>
            </Box>
          )}
          {!batches.loading && batches.data.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sent</TableCell>
                  <TableCell>Sent by</TableCell>
                  <TableCell align="right">Recipients</TableCell>
                  <TableCell align="right">Responses</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.data.map(batch => (
                  <TableRow key={batch.id}>
                    <TableCell>
                      {batch.sent_at
                        ? new Date(batch.sent_at).toLocaleString()
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {batch.sent_by
                        ? `${batch.sent_by.first_name} ${batch.sent_by.last_name}`
                        : '—'}
                    </TableCell>
                    <TableCell align="right">{batch.recipient_count}</TableCell>
                    <TableCell align="right">{batch.responded_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
